import { useState, useEffect, useRef } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { ExternalLink, ShoppingCart, Receipt, Package, Upload, X, Camera, CheckCircle2, AlertTriangle } from "lucide-react";
import { useTranslation } from "@/hooks/useTranslation";
import { useCreateCleanerPurchase, type CleanerPurchaseDeliveryStatus, type TurnoverPurchase } from "@/hooks/useCleanerPurchases";
import { useCleanerRecord } from "@/hooks/useCleaner";
import { getInStockCount, getOrderedCount } from "@/lib/inventoryQuantity";
import { toast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";

const RECEIPT_TOTAL_TOLERANCE_CENTS = 5;
type ReceiptTotalStatus = "matched" | "mismatch" | "unknown";

interface InventoryItem {
  id: string;
  item_name: string;
  in_stock_qty?: number | null;
  ordered_qty?: number | null;
  current_count?: number | null;
  threshold_count: number | null;
  vendor_url?: string | null;
  vendor_label?: string | null;
  photo_url?: string | null;
}

interface InventoryItemSheetProps {
  item: InventoryItem | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** When "request" or "purchase", sheet opens directly in that form (no action picker). */
  initialActionMode?: "request" | "purchase" | "idle";
  /** When set, this item already has a request_order for the current turnover — hide duplicate request. */
  existingRequestOrder?: TurnoverPurchase | null;
  propertyId: string;
  cleanerId: string;
  reimbursementMode: string;
  /** When set, reimbursement is attached to this task and its invoice period */
  cleaningTaskId?: string | null;
  scheduledDate?: string | null;
}

type ActionMode = "idle" | "request" | "purchase";

const InventoryItemSheet = ({
  item,
  open,
  onOpenChange,
  initialActionMode = "idle",
  existingRequestOrder = null,
  propertyId,
  cleanerId,
  reimbursementMode,
  cleaningTaskId,
  scheduledDate,
}: InventoryItemSheetProps) => {
  const { t, tDynamic } = useTranslation();
  const { data: cleanerRecord } = useCleanerRecord();
  const effectiveCleanerId = cleanerRecord?.id || cleanerId;
  const vendorOptions = [
    { value: "amazon", label: t("vendorAmazon") },
    { value: "store", label: t("vendorStore") },
    { value: "other", label: t("vendorOther") },
  ];
  const createPurchase = useCreateCleanerPurchase();
  const [actionMode, setActionMode] = useState<ActionMode>("idle");
  const [quantity, setQuantity] = useState(1);
  const [amount, setAmount] = useState("");
  const [note, setNote] = useState("");
  const [receiptFile, setReceiptFile] = useState<File | null>(null);
  const [deliveryStatus, setDeliveryStatus] = useState<CleanerPurchaseDeliveryStatus>("delivered");
  const [vendorSource, setVendorSource] = useState<string>("");
  const [proofFile, setProofFile] = useState<File | null>(null);
  const [uploading, setUploading] = useState(false);
  const [receiptUploadedUrl, setReceiptUploadedUrl] = useState<string | null>(null);
  const [receiptAnalysis, setReceiptAnalysis] = useState<{ total_cents: number; currency: string; confidence: number; source: string } | null>(null);
  const [receiptAnalysisLoading, setReceiptAnalysisLoading] = useState(false);
  const amountInputRef = useRef<HTMLInputElement>(null);

  const resetForm = () => {
    setActionMode("idle");
    setQuantity(1);
    setAmount("");
    setNote("");
    setReceiptFile(null);
    setReceiptUploadedUrl(null);
    setReceiptAnalysis(null);
    setDeliveryStatus("delivered");
    setVendorSource("");
    setProofFile(null);
  };
  const handleClose = () => { resetForm(); onOpenChange(false); };

  // Open directly in request or purchase form when initialActionMode is set
  useEffect(() => {
    if (!open || !item) return;
    if (existingRequestOrder && initialActionMode === "request") {
      setActionMode("idle");
      return;
    }
    if (initialActionMode !== "idle") {
      setActionMode(initialActionMode);
      if (initialActionMode === "request") {
        const current = getInStockCount(item);
        const threshold = item.threshold_count;
        if (threshold != null && typeof threshold === "number") {
          const needed = Math.max(1, threshold - current);
          setQuantity(needed);
        } else {
          setQuantity(1);
        }
      }
    } else {
      setActionMode("idle");
    }
  }, [open, item?.id, initialActionMode, existingRequestOrder?.id]);

  // After receipt upload: debounced upload + analyze-receipt-total
  useEffect(() => {
    if (!receiptFile) {
      setReceiptUploadedUrl(null);
      setReceiptAnalysis(null);
      return;
    }
    const timer = setTimeout(async () => {
      setReceiptAnalysisLoading(true);
      setReceiptAnalysis(null);
      try {
        const url = await uploadReceipt(receiptFile);
        if (url) {
          setReceiptUploadedUrl(url);
          const { data, error } = await supabase.functions.invoke("analyze-receipt-total", {
            body: { receiptUrl: url },
          });
          if (!error && data?.total_cents != null) {
            setReceiptAnalysis({
              total_cents: data.total_cents,
              currency: data.currency ?? "USD",
              confidence: typeof data.confidence === "number" ? data.confidence : 0.5,
              source: data.source ?? "ai",
            });
          }
        }
      } catch {
        setReceiptAnalysis(null);
      } finally {
        setReceiptAnalysisLoading(false);
      }
    }, 800);
    return () => clearTimeout(timer);
  }, [receiptFile?.name, receiptFile?.size]);

  const getStatus = () => {
    if (!item) return "ok";
    const current = getInStockCount(item);
    const threshold = item.threshold_count;
    if (threshold == null) return "ok";
    if (current === 0) return "out";
    if (current <= threshold) return "low";
    return "ok";
  };

  const uploadReceipt = async (file: File): Promise<string | null> => {
    const fileExt = file.name.split(".").pop();
    const fileName = `${propertyId}/${effectiveCleanerId}/${Date.now()}.${fileExt}`;
    const { error } = await supabase.storage.from("cleaning-photos").upload(fileName, file);
    if (error) return null;
    const { data: urlData } = supabase.storage.from("cleaning-photos").getPublicUrl(fileName);
    return urlData.publicUrl;
  };

  const uploadProof = async (file: File): Promise<string | null> => {
    const fileExt = file.name.split(".").pop();
    const fileName = `${propertyId}/${effectiveCleanerId}/proof/${Date.now()}.${fileExt}`;
    const { error } = await supabase.storage.from("cleaning-photos").upload(fileName, file);
    if (error) return null;
    const { data: urlData } = supabase.storage.from("cleaning-photos").getPublicUrl(fileName);
    return urlData.publicUrl;
  };

  const handleSubmitRequest = async () => {
    if (!item) return;
    if (!effectiveCleanerId) {
      toast({
        title: tDynamic("Error"),
        description: tDynamic("Cleaner profile not found. Ask the host to confirm your invite, then sign out and back in."),
        variant: "destructive",
      });
      return;
    }
    try {
      await createPurchase.mutateAsync({
        propertyId,
        cleanerId: effectiveCleanerId,
        itemId: item.id,
        quantity,
        amount: 0,
        note: note || undefined,
        type: "request_order",
        status: "pending_approval",
        cleaningTaskId: cleaningTaskId ?? undefined,
        scheduledDate: scheduledDate ?? undefined,
      });
      toast({ title: tDynamic("Request sent to owner") });
      handleClose();
    } catch (err) {
      console.error("[InventoryItemSheet] request_order failed:", err);
      const detail =
        err instanceof Error
          ? err.message
          : tDynamic("Failed to send request. Check your connection or ask the host to confirm your property access.");
      toast({ title: tDynamic("Error"), description: detail, variant: "destructive" });
    }
  };

  const enteredAmountCents = (() => {
    const a = parseFloat(amount);
    return Number.isFinite(a) && a >= 0 ? Math.round(a * 100) : null;
  })();
  const receiptTotalStatus: ReceiptTotalStatus = (() => {
    if (!receiptAnalysis) return "unknown";
    if (receiptAnalysis.confidence < 0.5) return "unknown";
    if (enteredAmountCents == null) return "unknown";
    const diff = Math.abs(enteredAmountCents - receiptAnalysis.total_cents);
    return diff <= RECEIPT_TOTAL_TOLERANCE_CENTS ? "matched" : "mismatch";
  })();

  const runReceiptAnalysis = async (file: File): Promise<typeof receiptAnalysis> => {
    const url = await uploadReceipt(file);
    if (!url) return null;
    setReceiptUploadedUrl(url);
    const { data, error } = await supabase.functions.invoke("analyze-receipt-total", {
      body: { receiptUrl: url },
    });
    if (error || data?.total_cents == null) return null;
    const result = {
      total_cents: data.total_cents,
      currency: data.currency ?? "USD",
      confidence: typeof data.confidence === "number" ? data.confidence : 0.5,
      source: data.source ?? "ai",
    };
    setReceiptAnalysis(result);
    return result;
  };

  const handleSubmitPurchase = async () => {
    if (!item) return;
    const parsedAmount = parseFloat(amount);
    if (isNaN(parsedAmount) || parsedAmount <= 0) { toast({ title: tDynamic("Invalid amount"), variant: "destructive" }); return; }
    if (!receiptFile) { toast({ title: tDynamic("Receipt required"), variant: "destructive" }); return; }
    if (deliveryStatus === "delivered" && !proofFile) { toast({ title: tDynamic("Proof photo required for in-stock purchases"), variant: "destructive" }); return; }
    setUploading(true);
    try {
      let analysis = receiptAnalysis;
      let finalStatus = receiptTotalStatus;
      if (receiptFile && !analysis && !receiptAnalysisLoading) {
        setReceiptAnalysisLoading(true);
        analysis = await runReceiptAnalysis(receiptFile) ?? null;
        setReceiptAnalysisLoading(false);
        if (analysis) {
          const diff = Math.abs(Math.round(parsedAmount * 100) - analysis.total_cents);
          finalStatus = analysis.confidence < 0.5 ? "unknown" : diff <= RECEIPT_TOTAL_TOLERANCE_CENTS ? "matched" : "mismatch";
        } else {
          finalStatus = "unknown";
        }
      }

      const receiptUrl = receiptUploadedUrl || (await uploadReceipt(receiptFile)) || undefined;
      let proofUrl: string | null = null;
      if (deliveryStatus === "delivered" && proofFile) {
        proofUrl = await uploadProof(proofFile) ?? null;
        if (!proofUrl) { toast({ title: tDynamic("Failed to upload proof photo"), variant: "destructive" }); setUploading(false); return; }
      }
      const result = await createPurchase.mutateAsync({
        propertyId,
        cleanerId: effectiveCleanerId,
        itemId: item.id,
        quantity,
        amount: parsedAmount,
        receiptUrl,
        note: note || undefined,
        type: "cleaner_purchase",
        status: "pending_approval",
        cleaningTaskId: cleaningTaskId ?? undefined,
        scheduledDate: scheduledDate ?? undefined,
        deliveryStatus,
        proofUrl: proofUrl ?? undefined,
        deliveredAt: deliveryStatus === "delivered" ? new Date().toISOString() : null,
        vendorSource: vendorSource || undefined,
        receiptTotalCents: analysis?.total_cents ?? null,
        receiptTotalConfidence: analysis?.confidence ?? null,
        receiptTotalStatus: finalStatus,
        receiptTotalSource: analysis?.source ?? null,
      });

      const chargeResult = result?.chargeResult;
      if (reimbursementMode === "disabled") {
        toast({ title: tDynamic("Logged for tracking (no reimbursement)") });
      } else if (chargeResult?.charged) {
        toast({ title: tDynamic("Auto-paid") + " ✅", description: tDynamic("Charged to owner's card") });
      } else {
        toast({
          title: tDynamic("Submitted — pending approval"),
          description: chargeResult?.reason ? String(chargeResult.reason) : undefined,
        });
      }
      handleClose();
    } catch { toast({ title: tDynamic("Error"), variant: "destructive" }); } finally { setUploading(false); }
  };

  if (!item) return null;
  const status = getStatus();
  const current = getInStockCount(item);
  const ordered = getOrderedCount(item);
  const threshold = item.threshold_count;

  return (
    <Sheet open={open} onOpenChange={handleClose}>
      <SheetContent side="bottom" className="h-auto max-h-[90vh] rounded-t-xl overflow-y-auto">
        <SheetHeader className="text-left pb-4">
          <SheetTitle className="flex items-center gap-2"><Package className="w-5 h-5" />{tDynamic(item.item_name)}</SheetTitle>
        </SheetHeader>
        <div className="space-y-4 pb-6">
          {/* Item photo */}
          {item.photo_url && (
            <div className="flex justify-center">
              <img
                src={item.photo_url}
                alt={item.item_name}
                className="w-full max-w-[200px] h-auto max-h-[150px] object-contain rounded-lg border"
              />
            </div>
          )}
          
          <div className="flex items-center justify-between p-3 bg-muted/50 rounded-lg flex-wrap gap-2">
            <div><p className="text-sm text-muted-foreground">{tDynamic("In stock")}</p><p className="text-2xl font-bold">{current}</p></div>
            {ordered > 0 && <div><p className="text-sm text-muted-foreground">{tDynamic("Ordered (not arrived)")}</p><p className="text-lg font-medium">{ordered}</p></div>}
            <div className="text-right"><p className="text-sm text-muted-foreground">{tDynamic("Threshold")}</p><p className="text-lg font-medium">{threshold != null ? threshold : "—"}</p></div>
            <Badge className={status === "ok" ? "bg-green-100 text-green-700" : status === "low" ? "bg-amber-100 text-amber-700" : "bg-red-100 text-red-700"}>{status === "ok" ? tDynamic("OK") : status === "low" ? tDynamic("LOW") : tDynamic("Out")}</Badge>
          </div>
          {item.vendor_url && <Button variant="outline" className="w-full justify-start gap-2" onClick={() => window.open(item.vendor_url!, "_blank")}><ExternalLink className="w-4 h-4" />{tDynamic("View on")} {item.vendor_label || t("vendor")}</Button>}
          {actionMode === "idle" &&
            (initialActionMode === "idle" || (initialActionMode === "request" && !!existingRequestOrder)) && (
            <div className="space-y-3 pt-2">
              {existingRequestOrder ? (
                <div className="flex items-start gap-3 rounded-lg border border-emerald-200/80 bg-emerald-50/90 p-4 dark:border-emerald-800/50 dark:bg-emerald-950/35">
                  <CheckCircle2 className="mt-0.5 h-5 w-5 shrink-0 text-emerald-600 dark:text-emerald-400" aria-hidden />
                  <div className="min-w-0 space-y-1">
                    <p className="font-medium text-emerald-900 dark:text-emerald-100">{tDynamic("Request made")}</p>
                    <p className="text-sm text-emerald-800/90 dark:text-emerald-200/90">
                      {tDynamic("Owner was notified.")}{" "}
                      {typeof existingRequestOrder.quantity === "number" && existingRequestOrder.quantity > 0
                        ? `${tDynamic("Qty")} ${existingRequestOrder.quantity}.`
                        : null}
                    </p>
                  </div>
                </div>
              ) : null}
              {!existingRequestOrder && (
                <Button variant="outline" className="w-full h-14 justify-start gap-3" onClick={() => setActionMode("request")}>
                  <ShoppingCart className="w-5 h-5" />
                  <div className="text-left">
                    <p className="font-medium">{tDynamic("Request owner to order")}</p>
                    <p className="text-xs text-muted-foreground">{tDynamic("Notify owner to restock this item")}</p>
                  </div>
                </Button>
              )}
              <Button variant="outline" className="w-full h-14 justify-start gap-3" onClick={() => setActionMode("purchase")}>
                <Receipt className="w-5 h-5" />
                <div className="text-left">
                  <p className="font-medium">{tDynamic("I purchased this myself")}</p>
                  <p className="text-xs text-muted-foreground">
                    {reimbursementMode === "disabled" ? tDynamic("Log for tracking (no payment)") : tDynamic("Submit for reimbursement")}
                  </p>
                </div>
              </Button>
            </div>
          )}
          {actionMode === "request" && !existingRequestOrder && (
            <div className="space-y-4 pt-2">
              <div><Label>{tDynamic("Quantity needed")}</Label><Input type="number" min={1} value={quantity} onChange={(e) => setQuantity(parseInt(e.target.value) || 1)} className="mt-1.5 h-12" /></div>
              <div><Label>{tDynamic("Note (optional)")}</Label><Textarea value={note} onChange={(e) => setNote(e.target.value)} placeholder={tDynamic("Any additional details...")} className="mt-1.5" rows={2} /></div>
              <div className="flex gap-2 pt-2"><Button variant="outline" onClick={resetForm} className="flex-1">{tDynamic("Cancel")}</Button><Button onClick={handleSubmitRequest} disabled={createPurchase.isPending} className="flex-1">{createPurchase.isPending ? tDynamic("Sending...") : tDynamic("Send Request")}</Button></div>
            </div>
          )}
          {actionMode === "purchase" && (
            <div className="space-y-4 pt-2">
              <div>
                <Label>{tDynamic("In stock or ordered?")}</Label>
                <Select value={deliveryStatus} onValueChange={(v) => setDeliveryStatus(v as CleanerPurchaseDeliveryStatus)}>
                  <SelectTrigger className="mt-1.5 h-12">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="delivered">{tDynamic("In stock")}</SelectItem>
                    <SelectItem value="ordered">{tDynamic("Ordered (not arrived)")}</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>{tDynamic("Vendor")}</Label>
                <Select value={vendorSource || "none"} onValueChange={(v) => setVendorSource(v === "none" ? "" : v)}>
                  <SelectTrigger className="mt-1.5 h-12">
                    <SelectValue placeholder={tDynamic("Select vendor (optional)")} />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">{tDynamic("— Optional —")}</SelectItem>
                    {vendorOptions.map((opt) => (
                      <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div><Label>{tDynamic("Quantity purchased")}</Label><Input type="number" min={1} value={quantity} onChange={(e) => setQuantity(parseInt(e.target.value) || 1)} className="mt-1.5 h-12" /></div>
              <div>
                <Label>{tDynamic("Amount paid ($)")}</Label>
                <Input
                  ref={amountInputRef}
                  type="number"
                  step="0.01"
                  min="0"
                  value={amount}
                  onChange={(e) => setAmount(e.target.value)}
                  placeholder="0.00"
                  className="mt-1.5 h-12"
                />
              </div>
              <div>
                <Label>{tDynamic("Receipt photo")} <span className="text-destructive">*</span></Label>
                <div className="mt-1.5">
                  {receiptFile ? (
                    <div className="flex items-center gap-2 p-3 bg-muted rounded-lg">
                      <Receipt className="w-5 h-5 text-muted-foreground" />
                      <span className="flex-1 truncate text-sm">{receiptFile.name}</span>
                      <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => setReceiptFile(null)}><X className="w-4 h-4" /></Button>
                    </div>
                  ) : (
                    <label className="flex items-center justify-center gap-2 h-12 border-2 border-dashed rounded-lg cursor-pointer hover:bg-muted/50">
                      <Upload className="w-5 h-5 text-muted-foreground" />
                      <span className="text-sm text-muted-foreground">{tDynamic("Upload receipt")}</span>
                      <input type="file" accept="image/*" className="hidden" onChange={(e) => setReceiptFile(e.target.files?.[0] || null)} />
                    </label>
                  )}
                </div>
                {receiptAnalysisLoading && (
                  <p className="text-xs text-muted-foreground mt-1.5 flex items-center gap-2">
                    <span className="inline-block w-4 h-4 border-2 border-primary border-t-transparent rounded-full animate-spin" />
                    {tDynamic("Analyzing receipt…")}
                  </p>
                )}
                {!receiptAnalysisLoading && receiptAnalysis && (
                  <div className="mt-2 p-2.5 rounded-lg bg-muted/60 text-sm">
                    <p className="font-medium">
                      {tDynamic("Receipt total detected")}: ${(receiptAnalysis.total_cents / 100).toFixed(2)}
                      {receiptAnalysis.confidence < 1 && (
                        <span className="text-muted-foreground font-normal ml-1">({Math.round(receiptAnalysis.confidence * 100)}%)</span>
                      )}
                    </p>
                    {receiptTotalStatus === "matched" && (
                      <p className="flex items-center gap-1.5 text-green-700 mt-1">
                        <CheckCircle2 className="w-4 h-4 shrink-0" />
                        {tDynamic("Receipt verified")} ✅
                      </p>
                    )}
                    {(receiptTotalStatus === "mismatch" || receiptTotalStatus === "unknown") && (
                      <p className="flex items-center gap-1.5 text-amber-700 mt-1 text-xs">
                        <AlertTriangle className="w-4 h-4 shrink-0" />
                        {tDynamic("Needs approval — couldn't verify total")} ⚠️
                      </p>
                    )}
                    {receiptTotalStatus === "mismatch" && (
                      <div className="mt-2 space-y-1.5">
                        <p className="text-amber-700 text-xs">
                          {tDynamic("Receipt total doesn't match entered amount.")}
                        </p>
                        <div className="flex flex-wrap gap-2">
                          <Button type="button" variant="outline" size="sm" className="h-8 text-xs" onClick={() => amountInputRef.current?.focus()}>
                            {tDynamic("Edit amount")}
                          </Button>
                          <Button type="button" variant="outline" size="sm" className="h-8 text-xs" onClick={() => setAmount((receiptAnalysis.total_cents / 100).toFixed(2))}>
                            {tDynamic("Use detected total")}
                          </Button>
                          <span className="text-xs text-muted-foreground self-center">{tDynamic("Submit anyway (requires owner approval)")}</span>
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </div>
              {deliveryStatus === "delivered" && (
                <div>
                  <Label>{tDynamic("Proof photo (item in stock)")} <span className="text-destructive">*</span></Label>
                  <div className="mt-1.5">
                    {proofFile ? (
                      <div className="flex items-center gap-2 p-3 bg-muted rounded-lg">
                        <Camera className="w-5 h-5 text-muted-foreground" />
                        <span className="flex-1 truncate text-sm">{proofFile.name}</span>
                        <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => setProofFile(null)}><X className="w-4 h-4" /></Button>
                      </div>
                    ) : (
                      <label className="flex items-center justify-center gap-2 h-12 border-2 border-dashed rounded-lg cursor-pointer hover:bg-muted/50">
                        <Camera className="w-5 h-5 text-muted-foreground" />
                        <span className="text-sm text-muted-foreground">{tDynamic("Upload proof photo")}</span>
                        <input type="file" accept="image/*" className="hidden" onChange={(e) => setProofFile(e.target.files?.[0] || null)} />
                      </label>
                    )}
                  </div>
                </div>
              )}
              <div><Label>{tDynamic("Note (optional)")}</Label><Textarea value={note} onChange={(e) => setNote(e.target.value)} placeholder={tDynamic("Any additional details...")} className="mt-1.5" rows={2} /></div>
              <div className="flex gap-2 pt-2"><Button variant="outline" onClick={resetForm} className="flex-1">{tDynamic("Cancel")}</Button><Button onClick={handleSubmitPurchase} disabled={createPurchase.isPending || uploading || (deliveryStatus === "delivered" && !proofFile)} className="flex-1">{uploading ? tDynamic("Uploading...") : createPurchase.isPending ? tDynamic("Submitting...") : reimbursementMode === "disabled" ? tDynamic("Log for tracking") : tDynamic("Submit Purchase")}</Button></div>
            </div>
          )}
        </div>
      </SheetContent>
    </Sheet>
  );
};

export default InventoryItemSheet;